import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Tabs } from 'antd';
import { Switch, Route, useRouteMatch, useHistory, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import RecruiterManagement from '../RecruiterManagement';
import MasterContracts from '../MasterContracts/MasterContracts';
import InvoicingPage from '../Invoicing';
import PermissionsSettings from '../PermissionsSettings';
import AnalyticsPage from '../Analytics';
import useHirePortContext from '../../hooks/useHirePortContext';
import { getAgencyPortalTabs, AgencyPortalTab } from './agencyPortalTabStrategies';

import {
  RECRUITER_MANAGEMENT,
  MASTER_CONTRACTS,
  PERMISSIONS_SETTINGS,
} from '../../constants/routes';

const { TabPane } = Tabs;

// Apply theme styling to Tabs
const StyledTabs = styled(Tabs)`
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primaryColor};
  }
  .ant-tabs-tab:hover {
    color: ${({ theme }) => theme.hoverColor};
  }
  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primaryColor};
  }
  // Add some padding below the tabs before the content
  .ant-tabs-nav {
    margin-bottom: 1.5rem !important; 
  }
`;

const PageContainer = styled.div`
  padding: 1.5rem;
`;

const EmbedAgencyPortal = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();

  // Determine which tabs should be visible for the current ATS (pack)
  const { atsType } = useHirePortContext();
  const visibleTabs = getAgencyPortalTabs(atsType);

  // Construct full paths for tabs
  const recruiterManagementPath = `${match.path}${RECRUITER_MANAGEMENT}`;
  const masterContractsPath = `${match.path}${MASTER_CONTRACTS}`;
  const invoicingPath = `${match.path}/invoicing`;
  const permissionsSettingsPath = `${match.path}${PERMISSIONS_SETTINGS}`;
  // Construct path for analytics as a subroute: /embed/:pack/agency-portal/analytics
  const agencyAnalyticsPath = `${match.path}/analytics`;

  // Helper map to link tab identifiers to their render data
  const allTabs: Record<AgencyPortalTab, { path: string; label: string }> = {
    recruiterManagement: { path: recruiterManagementPath, label: t('AGENCY_MANAGEMENT') },
    masterContracts: { path: masterContractsPath, label: t('AGENCY_CONTRACTS') },
    invoicing: { path: invoicingPath, label: t('HIRING_APPROVALS') },
    permissionsSettings: { path: permissionsSettingsPath, label: t('AGENCY_PORTAL_SETTINGS') },
    analytics: { path: agencyAnalyticsPath, label: t('AGENCY_ANALYTICS') },
  };

  // First visible tab becomes the default/fallback tab
  const defaultTabPath = allTabs[visibleTabs[0]].path;

  const activeKey = useMemo(() => {
    const currentFullPath = history.location.pathname;
    if (currentFullPath.startsWith(masterContractsPath)) return masterContractsPath;
    if (currentFullPath.startsWith(invoicingPath)) return invoicingPath;
    if (currentFullPath.startsWith(permissionsSettingsPath)) return permissionsSettingsPath;
    if (currentFullPath.startsWith(agencyAnalyticsPath)) return agencyAnalyticsPath;
    return defaultTabPath; // Default (first visible)
  }, [history.location.pathname, masterContractsPath, invoicingPath, permissionsSettingsPath, agencyAnalyticsPath, defaultTabPath]);

  const handleTabChange = (key: string) => {
    history.push(key); // Key is now the full path
  };

  return (
    <PageContainer>
      <StyledTabs activeKey={activeKey} onChange={handleTabChange}>
        {visibleTabs.map((tabKey) => (
          <TabPane
            key={allTabs[tabKey].path}
            tab={allTabs[tabKey].label}
          />
        ))}
      </StyledTabs>

      <Switch>
        <Route path={recruiterManagementPath} component={RecruiterManagement} />
        <Route path={masterContractsPath} component={MasterContracts} />
        <Route path={invoicingPath} component={InvoicingPage} />
        <Route
          path={permissionsSettingsPath}
          render={(props) => <PermissionsSettings {...props} hideMenu={true} />}
        />
        <Route
          path={agencyAnalyticsPath}
          render={(props) => <AnalyticsPage {...props} pack={undefined} />}
        />
        {/* Redirect base path (e.g., /embed/:pack/agency-portal) to the first visible tab */}
        <Redirect from={match.path} exact to={defaultTabPath} />
      </Switch>
    </PageContainer>
  );
};

export default EmbedAgencyPortal;